import React, { useState, useCallback, useMemo, useRef } from 'react';
import { StyleSheet, Animated, View, Platform, Pressable } from 'react-native';
import TextInput from '@/components/Typography/TextInput';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';

// Constants for animation
const ANIMATION_DURATION = 200;
const ANIMATION_CONFIG = {
  duration: ANIMATION_DURATION,
  useNativeDriver: Platform.OS !== 'web',
};

// Create styles outside component
const createStyles = (colors, isXS, isFullWidth) => StyleSheet.create({
  container: {
    position: 'relative',
    ...(Platform.OS === 'windows' ? { overflow: 'visible' } : {}),
    height: isXS ? 48 : 44,
    width: isFullWidth ? '100%' : 'auto',
  },
  label: {
    position: 'absolute',
    left: 16,
    top: isXS ? 13 : 14,
    fontSize: isXS ? 16 : 14,
    paddingRight: 6,
    backgroundColor: 'transparent',
    zIndex: 1,
    color: colors.text_color_tertiary,
  },
  input: {
    width: '100%',
    height: '100%',
    paddingHorizontal: 16,
    // Windows RichEdit starts at the content inset rather than centering a
    // single line. Leave room between the floating label and the entered text.
    paddingVertical: Platform.OS === 'windows' ? 12 : 0,
    borderWidth: isXS ? 1.5 : 1,
    borderRadius: 8,
    fontSize: isXS ? 16 : 14,
    backgroundColor: 'transparent',
  },
  animatedLabel: {
    paddingHorizontal: 4,
  },
  passwordToggle: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
  disabled: {
    opacity: 0.6,
    ...(Platform.OS === 'web' && { cursor: 'not-allowed' }),
  }
});

const FloatingLabelInput = React.forwardRef(({ 
  label, 
  value = '', 
  onChangeText, 
  secureTextEntry = false,
  keyboardType = 'default',
  autoCapitalize = 'none',
  isFullWidth = false,
  style,
  placeholder = '',
  testID,
  textContentType = 'none',
  autoComplete = 'off',
  returnKeyType = 'default',
  onSubmitEditing,
  blurOnSubmit,
  disabled = false,
  accessibilityLabel,
  accessibilityHint,
}, ref) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { isXS } = useResponsiveness();
  const inputRef = useRef(null);

  // Memoize styles
  const styles = useMemo(() => createStyles(colors, isXS, isFullWidth), [colors, isXS, isFullWidth]);

  // Memoize animated values
  const [labelPosition] = useState(() => new Animated.Value(value ? 1 : 0));
  const [labelSize] = useState(() => new Animated.Value(value ? 1 : 0));

  // Memoize animations
  const animateLabel = useCallback((toValue) => {
    if (disabled) return;
    
    Animated.parallel([
      Animated.timing(labelPosition, {
        toValue,
        ...ANIMATION_CONFIG,
      }),
      Animated.timing(labelSize, {
        toValue,
        ...ANIMATION_CONFIG,
      }),
    ]).start();
  }, [labelPosition, labelSize, disabled]);

  React.useEffect(() => {
    const shouldFloat = !!value || isFocused;

    if (disabled) {
      labelPosition.setValue(shouldFloat ? 1 : 0);
      labelSize.setValue(shouldFloat ? 1 : 0);
      return;
    }

    animateLabel(shouldFloat ? 1 : 0);
  }, [value, isFocused, disabled, animateLabel, labelPosition, labelSize]);

  // Memoize handlers
  const handleFocus = useCallback(() => {
    if (disabled) return;
    setIsFocused(true);
    animateLabel(1);
  }, [animateLabel, disabled]);

  const handleBlur = useCallback(() => {
    if (disabled) return;
    setIsFocused(false);
    if (!value) {
      animateLabel(0);
    }
  }, [value, animateLabel, disabled]);

  // Memoize label transform
  const labelTransform = useMemo(() => [
    {
      translateY: labelPosition.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -22],
      }),
    },
    {
      translateX: labelPosition.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -14],
      }),
    },
    {
      scale: labelSize.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 0.75],
      }),
    },
  ], [labelPosition, labelSize]);

  // Memoize label style
  const labelStyle = useMemo(() => [
    styles.label,
    {
      transform: labelTransform,
      color: disabled ? colors.text_color_tertiary : (isFocused ? colors.text_color_primary : colors.text_color_tertiary),
      backgroundColor: colors.solid_color_primary,
      ...styles.animatedLabel,
    },
  ], [styles, labelTransform, isFocused, colors, disabled]);

  // Memoize input style
  const inputStyle = useMemo(() => [
    styles.input,
    {
      borderColor: isFocused ? colors.text_color_primary : colors.border_color_secondary,
      color: disabled ? colors.text_color_tertiary : colors.text_color_primary,
    },
    disabled && styles.disabled,
    style,
  ], [styles, isFocused, colors, style, disabled]);

  // Add handler for label press
  const handleLabelPress = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  return (
    // Keep Fabric from flattening and rebuilding this wrapper when focus
    // changes the animated styles. Re-inserting the TextInput dismisses Android's keyboard.
    <View style={styles.container} testID={testID} {...(Platform.OS === 'web' ? {} : { collapsable: false })}>
      <Animated.Text style={labelStyle} onPress={handleLabelPress} accessible={false} aria-hidden={Platform.OS === 'web' ? true : undefined}>
        {label}
      </Animated.Text>
      <TextInput
        {...(Platform.OS === 'macos' || Platform.OS === 'windows' ? { enableFocusRing: false } : {})}
        ref={(node) => {
          inputRef.current = node;
          if (ref) {
            if (typeof ref === 'function') ref(node);
            else ref.current = node;
          }
        }}
        style={[inputStyle, secureTextEntry && { paddingRight: 48 }]}
        accessibilityLabel={accessibilityLabel || label}
        accessibilityHint={accessibilityHint}
        value={value}
        onChangeText={onChangeText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        secureTextEntry={secureTextEntry && (!passwordVisible || disabled)}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        autoCorrect={secureTextEntry ? false : undefined}
        spellCheck={secureTextEntry ? false : undefined}
        placeholder={!isFocused && !value ? placeholder : ''}
        placeholderTextColor={colors.color_input_placeholder}
        textContentType={textContentType}
        autoComplete={autoComplete}
        returnKeyType={returnKeyType}
        onSubmitEditing={onSubmitEditing}
        blurOnSubmit={blurOnSubmit}
        editable={!disabled}
        focusable={!disabled}
        pointerEvents={disabled ? 'none' : 'auto'}
      />
      {secureTextEntry && <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(passwordVisible && !disabled ? 'auth.hidePassword' : 'auth.showPassword')}
        accessibilityState={{ disabled }}
        disabled={disabled}
        onPress={() => setPasswordVisible(visible => !visible)}
        style={({ pressed }) => [styles.passwordToggle, pressed && { backgroundColor: colors.solid_color_secondary }, disabled && styles.disabled]}
      >
        <Icon name={passwordVisible && !disabled ? 'eye-slash' : 'eye'} size={18} color={colors.text_color_secondary} />
      </Pressable>}
    </View>
  );
});

FloatingLabelInput.displayName = 'FloatingLabelInput';

FloatingLabelInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChangeText: PropTypes.func.isRequired,
  secureTextEntry: PropTypes.bool,
  keyboardType: PropTypes.string,
  isFullWidth: PropTypes.bool,
  autoCapitalize: PropTypes.oneOf(['none', 'sentences', 'words', 'characters']),
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  placeholder: PropTypes.string,
  testID: PropTypes.string,
  textContentType: PropTypes.string,
  autoComplete: PropTypes.string,
  returnKeyType: PropTypes.string,
  onSubmitEditing: PropTypes.func,
  blurOnSubmit: PropTypes.bool,
  disabled: PropTypes.bool,
  accessibilityLabel: PropTypes.string,
  accessibilityHint: PropTypes.string,
};

export default React.memo(FloatingLabelInput); 
